import { useCallback, useEffect, useRef } from 'react';
import { muteErrorAndFetching, useFetchOld } from 'hooks';
import { useAppStore } from 'store/hooks';
import { getLiveSheet } from './actions';
/**
 * This does not subscribe to the store.
 * @returns The cached unexpired live sheet
 */
function useCachedLiveSheet() {
    const store = useAppStore();
    const { liveSheetCache, liveSheetCacheTimestamp } = store.getState().liveSheet;
    if (liveSheetCache &&
        liveSheetCacheTimestamp &&
        Date.now() - liveSheetCacheTimestamp < 300_000) {
        return liveSheetCache;
    }
    return undefined;
}
export function useLiveSheetUpdatedAtInterval() {
    const intervalRef = useRef([0, 0]); // [ms, repeat_count]
    const timeoutRef = useRef(undefined);
    const [status, , , doGetLiveSheet] = useFetchOld(muteErrorAndFetching);
    const startAtInterval = useCallback(() => {
        const update = () => {
            doGetLiveSheet(getLiveSheet());
            // increase interval over time
            intervalRef.current = (([ms, count]) => {
                if (ms < 1_000)
                    return [1_000, 0];
                if (count < 5)
                    return [ms, count + 1];
                return [ms > 60_000 ? ms : ms * 2, 0];
            })(intervalRef.current);
            timeoutRef.current = setTimeout(update, intervalRef.current[0]);
        };
        timeoutRef.current = setTimeout(update, intervalRef.current[0]);
    }, [doGetLiveSheet]);
    useEffect(() => {
        startAtInterval();
        return () => clearTimeout(timeoutRef.current);
    }, [startAtInterval]);
    const refreshNow = useCallback(() => {
        clearTimeout(timeoutRef.current);
        intervalRef.current = [0, 0];
        startAtInterval();
    }, [startAtInterval]);
    return [status, refreshNow];
}
export function useLiveSheetUpdatedAtIntervalInitWithCache() {
    const cachedLiveSheet = useCachedLiveSheet();
    const [liveSheetStatus, refreshLiveSheetStatus] = useLiveSheetUpdatedAtInterval();
    return [
        liveSheetStatus ?? cachedLiveSheet,
        !liveSheetStatus && !!cachedLiveSheet,
        refreshLiveSheetStatus,
    ];
}
